import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type {
  Author,
  ContractedBook,
  KarriereHendelse,
  KarriereHendelseType,
  LojalitetSample,
} from '@/data/types';

const SHEET_WIDTH = 1000;
const PADDING = 24;
const GRAF_HOYDE = 220;
const GRAF_DAGER_MAX = 90;
const KORT_HOYDE = 116;
const KORT_GAP = 16;

const IKON_PER_TYPE: Record<KarriereHendelseType, string> = {
  kontrahert: '📜',
  'bok-utgitt': '📚',
  'bok-bestselger': '⭐',
  'kurs-fullfort': '🎓',
  'pris-nominasjon': '🥈',
  'pris-vinner': '🏆',
  'lojalitet-skifte': '💛',
};

export class ForfatterKarriereModal {
  private layer?: Phaser.GameObjects.Container;
  private innholdContainer?: Phaser.GameObjects.Container;
  private mask?: Phaser.Display.Masks.GeometryMask;
  private scrollY = 0;
  private scrollMax = 0;
  private wheelHandler?: (
    _p: unknown,
    _g: unknown,
    _dx: number,
    dy: number,
  ) => void;
  private aktivForfatterId?: string;
  private sheetY = 96;
  private sheetH = GAME_HEIGHT - 192;
  private synligHoyde = 0;

  constructor(private scene: Phaser.Scene) {}

  open(authorId: string): void {
    if (this.layer && this.aktivForfatterId === authorId) return;
    this.close();
    this.aktivForfatterId = authorId;

    const snap = gameState.snapshot;
    const forfatter = snap.authors[authorId];
    if (!forfatter) return;
    const boker = gameState.bokerForForfatter(authorId);

    this.layer = this.scene.add.container(0, 0).setDepth(220);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheet = this.scene.add
      .rectangle(sheetX, this.sheetY, SHEET_WIDTH, this.sheetH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(4, COLORS.wine, 1);
    this.layer.add(sheet);

    const headerH = 160;
    const headerBg = this.scene.add
      .rectangle(sheetX, this.sheetY, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(headerBg);

    const tittel = this.scene.add
      .text(sheetX + PADDING, this.sheetY + 32, `Karriere — ${forfatter.navn}`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(40),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - PADDING * 2 - 90 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const undertekst = this.scene.add
      .text(
        sheetX + PADDING,
        this.sheetY + 112,
        `${boker.length} bøker · dag ${snap.dag}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(20),
          color: '#5a4a3a',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertekst);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - PADDING, this.sheetY + 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(56),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const innholdTop = this.sheetY + headerH;
    const innholdBunn = this.sheetY + this.sheetH - 24;
    this.synligHoyde = innholdBunn - innholdTop;

    const maskShape = this.scene.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(sheetX + 12, innholdTop, SHEET_WIDTH - 24, this.synligHoyde);
    this.mask = maskShape.createGeometryMask();

    const innhold = this.scene.add.container(sheetX + PADDING, innholdTop);
    innhold.setMask(this.mask);
    this.layer.add(innhold);
    this.innholdContainer = innhold;

    let y = 0;
    y = this.byggLojalitetsGraf(innhold, y, SHEET_WIDTH - PADDING * 2, forfatter);
    y += 20;
    y = this.byggSalgsStatistikk(innhold, y, SHEET_WIDTH - PADDING * 2, boker);
    y += 20;
    y = this.byggTidslinje(innhold, y, SHEET_WIDTH - PADDING * 2, forfatter);

    this.scrollMax = Math.max(0, y - this.synligHoyde + 24);
    this.bindScroll(backdrop, innholdTop);
  }

  close(): void {
    if (this.wheelHandler) {
      this.scene.input.off('wheel', this.wheelHandler);
      this.wheelHandler = undefined;
    }
    this.layer?.destroy(true);
    this.layer = undefined;
    this.innholdContainer = undefined;
    this.mask = undefined;
    this.scrollY = 0;
    this.scrollMax = 0;
    this.aktivForfatterId = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private bindScroll(
    backdrop: Phaser.GameObjects.Rectangle,
    innholdTop: number,
  ): void {
    this.wheelHandler = (_p, _g, _dx, dy) => {
      if (!this.innholdContainer) return;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
      this.innholdContainer.y = innholdTop - this.scrollY;
    };
    this.scene.input.on('wheel', this.wheelHandler);

    backdrop.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const startY = pointer.y;
      const startScroll = this.scrollY;
      const onMove = (p: Phaser.Input.Pointer): void => {
        const dy = p.y - startY;
        this.scrollY = Math.max(0, Math.min(this.scrollMax, startScroll - dy));
        if (this.innholdContainer) {
          this.innholdContainer.y = innholdTop - this.scrollY;
        }
      };
      const onUp = (): void => {
        this.scene.input.off('pointermove', onMove);
        this.scene.input.off('pointerup', onUp);
      };
      this.scene.input.on('pointermove', onMove);
      this.scene.input.on('pointerup', onUp);
    });
  }

  private byggLojalitetsGraf(
    container: Phaser.GameObjects.Container,
    y: number,
    w: number,
    forfatter: Author,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'Lojalitets-utvikling', {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    container.add(tittel);

    const grafY = y + 36;
    const grafH = GRAF_HOYDE;
    const grafW = w;

    const bg = this.scene.add
      .rectangle(0, grafY, grafW, grafH, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    container.add(bg);

    const innerPad = 30;
    const grafInnerX = innerPad;
    const grafInnerY = grafY + 16;
    const grafInnerW = grafW - innerPad * 2;
    const grafInnerH = grafH - 56;

    for (let i = 0; i <= 4; i += 1) {
      const ly = grafInnerY + (grafInnerH * i) / 4;
      const linje = this.scene.add
        .rectangle(grafInnerX, ly, grafInnerW, 1, COLORS.ochre, 0.25)
        .setOrigin(0, 0);
      container.add(linje);
      const label = this.scene.add
        .text(grafInnerX - 10, ly - 8, `${100 - i * 25}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#5a4a3a',
        })
        .setOrigin(1, 0);
      container.add(label);
    }

    const historikk = forfatter.lojalitetHistorikk ?? [];
    if (historikk.length === 0) {
      const tom = this.scene.add
        .text(grafW / 2, grafY + grafH / 2, 'Ingen lojalitets-historikk ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#7a7060',
          fontStyle: 'italic',
        })
        .setOrigin(0.5);
      container.add(tom);
      return y + 36 + grafH;
    }

    const samples = this.filtrerSamples(historikk);
    if (samples.length === 1) {
      const s = samples[0];
      const px = grafInnerX + grafInnerW / 2;
      const py = grafInnerY + grafInnerH * (1 - s.verdi / 100);
      const dot = this.scene.add.circle(px, py, 6, COLORS.wine, 1);
      container.add(dot);
      const label = this.scene.add
        .text(grafInnerX + grafInnerW / 2, grafY + grafH - 18, `Dag ${s.dag}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#5a4a3a',
        })
        .setOrigin(0.5, 0);
      container.add(label);
      return y + 36 + grafH;
    }

    const dagMin = samples[0].dag;
    const dagMax = samples[samples.length - 1].dag;
    const dagSpenn = Math.max(1, dagMax - dagMin);

    const punkter: Array<{ x: number; py: number; sample: LojalitetSample }> = [];
    for (const s of samples) {
      const x = grafInnerX + ((s.dag - dagMin) / dagSpenn) * grafInnerW;
      const py = grafInnerY + grafInnerH * (1 - s.verdi / 100);
      punkter.push({ x, py, sample: s });
    }

    const linje = this.scene.add.graphics();
    linje.lineStyle(3, COLORS.wine, 1);
    linje.beginPath();
    linje.moveTo(punkter[0].x, punkter[0].py);
    for (let i = 1; i < punkter.length; i += 1) {
      linje.lineTo(punkter[i].x, punkter[i].py);
    }
    linje.strokePath();
    container.add(linje);

    for (const p of punkter) {
      const dot = this.scene.add.circle(p.x, p.py, 4, COLORS.wine, 1);
      container.add(dot);
    }

    const labelStart = this.scene.add
      .text(grafInnerX, grafY + grafH - 18, `Dag ${dagMin}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(12),
        color: '#5a4a3a',
      })
      .setOrigin(0, 0);
    const labelSlutt = this.scene.add
      .text(grafInnerX + grafInnerW, grafY + grafH - 18, `Dag ${dagMax}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(12),
        color: '#5a4a3a',
      })
      .setOrigin(1, 0);
    container.add([labelStart, labelSlutt]);

    return y + 36 + grafH;
  }

  private filtrerSamples(samples: ReadonlyArray<LojalitetSample>): LojalitetSample[] {
    if (samples.length === 0) return [];
    const sortert = [...samples].sort((a, b) => a.dag - b.dag);
    const sisteDag = sortert[sortert.length - 1].dag;
    const grense = sisteDag - GRAF_DAGER_MAX;
    return sortert.filter((s) => s.dag >= grense);
  }

  private byggSalgsStatistikk(
    container: Phaser.GameObjects.Container,
    y: number,
    w: number,
    boker: ReadonlyArray<ContractedBook>,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'Salgs-statistikk', {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    container.add(tittel);

    const boksY = y + 36;
    const boksH = 130;
    const bg = this.scene.add
      .rectangle(0, boksY, w, boksH, COLORS.beige, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    container.add(bg);

    let totalSalg = 0;
    let beste: ContractedBook | null = null;
    let snittSum = 0;
    let snittTeller = 0;
    for (const b of boker) {
      totalSalg += b.salg;
      if (!beste || b.salg > beste.salg) beste = b;
      for (const a of b.anmeldelser) {
        snittSum += a.terninger;
        snittTeller += 1;
      }
    }
    const snitt = snittTeller > 0 ? snittSum / snittTeller : 0;

    const kolW = w / 3;
    const labels: Array<{ x: number; tittel: string; verdi: string }> = [
      { x: PADDING, tittel: 'Total salg', verdi: totalSalg.toLocaleString('nb-NO') },
      {
        x: PADDING + kolW,
        tittel: 'Beste bok',
        verdi: beste
          ? `«${beste.title}» (${beste.salg.toLocaleString('nb-NO')})`
          : '—',
      },
      {
        x: PADDING + kolW * 2,
        tittel: 'Snitt anmeldelse',
        verdi: snittTeller > 0 ? `${snitt.toFixed(1)} / 6` : '—',
      },
    ];
    for (const k of labels) {
      const l = this.scene.add
        .text(k.x, boksY + 20, k.tittel, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#5a4a3a',
          fontStyle: '600',
        })
        .setOrigin(0, 0);
      const v = this.scene.add
        .text(k.x, boksY + 50, k.verdi, {
          fontFamily: FONTS.display,
          fontSize: fontStr(22),
          color: '#1a1a1a',
          wordWrap: { width: kolW - PADDING * 2 },
        })
        .setOrigin(0, 0);
      container.add([l, v]);
    }

    return boksY + boksH;
  }

  private byggTidslinje(
    container: Phaser.GameObjects.Container,
    y: number,
    w: number,
    forfatter: Author,
  ): number {
    const tittel = this.scene.add
      .text(0, y, 'Tidslinje', {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    container.add(tittel);

    let cursorY = y + 36;
    const karriere = forfatter.karriere ?? [];

    if (karriere.length === 0) {
      const tom = this.scene.add
        .text(w / 2, cursorY + 40, 'Ingen karriere-hendelser ennå.', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#7a7060',
          fontStyle: 'italic',
        })
        .setOrigin(0.5, 0);
      container.add(tom);
      return cursorY + 80;
    }

    const sortert = [...karriere].sort((a, b) => b.dag - a.dag);

    for (const h of sortert) {
      this.byggHendelseKort(container, cursorY, w, h);
      cursorY += KORT_HOYDE + KORT_GAP;
    }

    return cursorY;
  }

  private byggHendelseKort(
    container: Phaser.GameObjects.Container,
    y: number,
    w: number,
    h: KarriereHendelse,
  ): void {
    const bg = this.scene.add
      .rectangle(0, y, w, KORT_HOYDE, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.3);
    container.add(bg);

    const venstre = this.scene.add
      .rectangle(0, y, 4, KORT_HOYDE, COLORS.ochre, 1)
      .setOrigin(0, 0);
    container.add(venstre);

    const ikon = this.scene.add
      .text(24, y + KORT_HOYDE / 2, IKON_PER_TYPE[h.type] ?? '•', {
        fontFamily: FONTS.body,
        fontSize: fontStr(32),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0.5);
    container.add(ikon);

    const dagLabel = this.scene.add
      .text(86, y + 16, `Dag ${h.dag}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: '#5a4a3a',
        fontStyle: '700',
      })
      .setOrigin(0, 0);
    container.add(dagLabel);

    const beskrivelse = this.scene.add
      .text(86, y + 52, h.beskrivelse, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        wordWrap: { width: w - 106 },
      })
      .setOrigin(0, 0);
    container.add(beskrivelse);
  }
}
